import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { FormHelper } from '@helpers/form.helper';
import { CUSTOM_MASK } from '@helpers/custom-validator';
import { enumEntityDataSource } from '@data-layer/enums/isf/enumEntityDataSource';
import { enumEntityType } from '@data-layer/enums/isf/enumEntityType';
import { DESC_ENTITY } from '@constants/desc-entity';
import { CodeRepository } from '@data-layer/repositories';
import { enumEntityModelCode } from '@data-layer/enums/entities';
import { enumIdentifierQualifier } from '@data-layer/enums/common';
import { EnumHelper } from '@helpers/enum.helper';
import { IItemMask } from '@interfaces/item-mask';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { EntityModel } from '@data-layer/models/entities';
import { IsfStateService } from '@shared/isf-mutual/services';
import { CANADA_COUNTRY_CODE, MEXICO_COUNTRY_CODE, USA_COUNTRY_CODE } from '@constants/constants';
import { CountryModel } from '@data-layer/models/common';
import { omit } from 'lodash-es';
import { CompaniesService } from '@services/companies.service';
import { CompanyModel } from '@data-layer/models/companies';

@Component({
  selector: 'entity-unify-update',
  templateUrl: './entity-unify-update.component.html',
  styleUrls: ['./entity-unify-update.component.scss'],
})
export class EntityUnifyUpdateComponent implements OnInit, OnDestroy, AfterViewChecked {
  @Input() public isfEntity: EntityModel;
  @Input() public formGroup: UntypedFormGroup;
  @Input() public errors: { [name: string]: IErrorForm } = {};
  @Input() public code: enumEntityModelCode | string = '';
  @Input() public isDesktop = false;
  @Input() public isClearCountry: boolean = true;
  @Output() public areaClicked: EventEmitter<void> = new EventEmitter<void>();
  @Output() public formResetted: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('identifierInputElement')
  public identifierInputElement: ElementRef;
  @ViewChild('nameInputElement')
  public nameInputElement: ElementRef;

  public company: CompanyModel;
  public label: string;
  public info = null;
  public dataSourceControl: AbstractControl;
  public companyName: AbstractControl;
  public enumEntityDataSource = enumEntityDataSource;
  public enumEntityModelCode = enumEntityModelCode;
  public isShowAddress = true;
  public companyLoading = false;
  public identifierLoading: boolean = false;
  public defaultCountry = null;
  public CUSTOM_MASK = CUSTOM_MASK;
  public isfEntities: null | any[] = null;
  public identifierSearchResult: EntityModel[] = [];
  public type: enumEntityType;
  public entityIdentifierQualifiers: enumIdentifierQualifier[] = [enumIdentifierQualifier.EIN, enumIdentifierQualifier.SSN];
  public identifierItemMask: IItemMask = {} as IItemMask;
  private subscription: Subscription[] = [];
  private buyerEntities = [
    enumEntityModelCode.IMPORTER,
    enumEntityModelCode.CONSIGNEE,
    enumEntityModelCode.BUYER,
    enumEntityModelCode.SHIP_TO,
  ];
  private sellerEntities: enumEntityModelCode[] = [
    enumEntityModelCode.MANUFACTURER,
    enumEntityModelCode.CONSOLIDATOR,
    enumEntityModelCode.SELLER,
    enumEntityModelCode.STAFFING_LOCATION,
  ];
  private maskArray: { [key: string]: IItemMask } = {
    ANI: {
      mask: '000000-00000',
      placeholder: 'YYDDPP-NNNNN',
      inputmode: 'text',
      maskErrorTooltip: 'MASK_ERROR.ANI',
      maskMaxLength: 11,
    },
    EIN: {
      mask: '00-0000000AA',
      placeholder: 'NN-NNNNNNNAA',
      inputmode: 'text',
      maskErrorTooltip: 'MASK_ERROR.EIN',
      maskMaxLength: 11,
    },
    SSN: {
      mask: '000-00-0000',
      placeholder: 'NNN-NN-NNNN',
      inputmode: 'numeric',
      maskErrorTooltip: 'MASK_ERROR.ONLY_DIGITS',
      maskMaxLength: 9,
    },
  };
  private isIdentifierInit: boolean = false;
  private isNameInit: boolean = false;

  constructor(
    private fh: FormHelper,
    private cd: ChangeDetectorRef,
    private isfStateService: IsfStateService,
    private codeRepository: CodeRepository,
    private companiesService: CompaniesService,
  ) {}

  public ngOnInit(): void {
    this.info = this.isfEntity && DESC_ENTITY[this.isfEntity.entityCode];

    this.type = this.buyerEntities.indexOf(this.isfEntity.entityCode) !== -1 ? enumEntityType.buyer : enumEntityType.seller;
    if (this.type === enumEntityType.buyer) {
      this.defaultCountry = 'US';
    }

    this.label = `INFO.${this.code.toLocaleUpperCase()}.${this.isfEntity.id ? 'EDIT' : 'ADD_NEW'}`;
    this.dataSourceControl = this.formGroup.get('dataSource');
    this.companyName = this.formGroup.get('name');

    this.identifierItemMask =
      this.maskArray[EnumHelper.getEnumKeyByValue(enumIdentifierQualifier, this.formGroup.get('entityIdentifierQualifier').value)];

    if (this.isfEntity.entityCode === enumEntityModelCode.IMPORTER) {
      this.entityIdentifierQualifiers.push(enumIdentifierQualifier.ANI);
    }

    this.subscription.push(this.updateValueByDataSource(), this.updateIdentifierQualMask());

    // #5636
    /*if (this.isfEntity.entityCode === enumEntityModelCode.CONSIGNEE) {
      this.formGroup.get('address').reset();
      this.isShowAddress = false;
    }*/

    this.subscription.push(
      this.formGroup.get(['address', 'country']).valueChanges.subscribe((value) => {
        if (!value && this.formGroup.get(['address', 'state']).value) {
          this.formGroup.get(['address', 'state']).reset();
        }
      }),
    );

    this.company = this.companiesService.getCompany();
  }

  public ngOnDestroy(): void {
    this.subscription.forEach((e) => e.unsubscribe());
  }

  public ngAfterViewChecked() {
    if (!this.isNameInit && this.nameInputElement?.nativeElement) {
      this.subscription.push(this.searchEntityByCompanyName());
      this.isNameInit = true;
    }
    if (!this.isIdentifierInit && this.identifierInputElement?.nativeElement) {
      this.subscription.push(this.searchEntityByIdentifier());
      this.isIdentifierInit = true;
    }
  }

  public get isShowState(): boolean {
    const isSeller = this.sellerEntities.includes(this.isfEntity.entityCode);
    const country = this.formGroup.get(['address', 'country']).value;

    return (
      ([
        enumEntityModelCode.SELLER,
        enumEntityModelCode.MANUFACTURER,
        enumEntityModelCode.CONSOLIDATOR,
        enumEntityModelCode.STAFFING_LOCATION,
      ].includes(this.isfEntity.entityCode) &&
        country &&
        [CANADA_COUNTRY_CODE, USA_COUNTRY_CODE, MEXICO_COUNTRY_CODE].includes(country.code)) ||
      !isSeller
    );
  }

  public get isForeignImporter(): boolean {
    return (
      (this.company?.identifierQualifier === enumIdentifierQualifier.ANI ||
        this.formGroup.get('entityIdentifierQualifier').value === enumIdentifierQualifier.ANI) &&
      [enumEntityModelCode.IMPORTER, enumEntityModelCode.BUYER, enumEntityModelCode.SHIP_TO].includes(this.isfEntity.entityCode)
    );
  }

  public get isConsigneeEntity(): boolean {
    return enumEntityModelCode.CONSIGNEE === this.isfEntity.entityCode;
  }

  public get showIdentifier(): boolean {
    return [enumEntityModelCode.IMPORTER, enumEntityModelCode.CONSIGNEE, enumEntityModelCode.BUYER].includes(this.isfEntity.entityCode);
  }

  public get showDateOfBirth(): boolean {
    return this.formGroup.get('entityIdentifierQualifier').value === enumIdentifierQualifier.SSN;
  }

  public get helperIdentifier(): string {
    const value: enumIdentifierQualifier = this.formGroup.get('entityIdentifierQualifier').value;
    return !value ? null : 'FORM_HELPER.IDENTIFIER_' + EnumHelper.getEnumKeyByValue(enumIdentifierQualifier, value);
  }

  public setIdentitiesQual(qual: enumIdentifierQualifier) {
    this.formGroup.get('entityIdentifierQualifier').patchValue(qual);
  }

  public changeAddress(item: EntityModel) {
    this.companyName.patchValue(item.name, { emitEvent: false });
    if ((!item.address || !item.address.country || !item.address.country.code) && this.type === enumEntityType.buyer) {
      this.getDefaultCountryAddress()
        .pipe(take(1))
        .subscribe((country) => {
          item.address.country = country;
          this.formGroup.get('entityIdentifier').patchValue(item.entityIdentifier, { emitEvent: false });
          this.formGroup.patchValue(omit(item, ['entityIdentifier', 'name']));

          this.cd.markForCheck();
        });

      return;
    }
    this.formGroup.get('entityIdentifier').patchValue(item.entityIdentifier, { emitEvent: false });
    this.formGroup.patchValue(omit(item, ['entityIdentifier', 'name']));
  }

  public getDefaultCountryAddress(): Observable<CountryModel> {
    return this.codeRepository.getCountry(this.defaultCountry);
  }

  public resetForm(): void {
    this.formGroup.reset({
      id: this.formGroup.get('id').value || null,
      address: {
        ...Object.keys(this.formGroup.get('address').value).map((item) => ({ [item]: null })),
        // #5326
        // country: this.formGroup.get('address').value?.country,
        country: this.isClearCountry ? null : this.formGroup.get('address').value?.country,
      },
      entityIdentifierQualifier: this.formGroup.get('entityIdentifierQualifier').value,
    });

    this.formResetted.emit();
  }

  public get entityIdentifierLabel(): string {
    switch (this.formGroup.get('entityIdentifierQualifier').value) {
      case enumIdentifierQualifier.EIN:
        return 'LABELS.EIN_HINT';
      case enumIdentifierQualifier.SSN:
        return 'LABELS.SSN_HINT';
      case enumIdentifierQualifier.ANI:
        return 'LABELS.ANI_HINT';
      default:
        return 'LABELS.EIN_HINT';
    }
  }

  private searchEntityByCompanyName() {
    return this.formGroup
      .get('name')
      .valueChanges.pipe(
        distinctUntilChanged(),
        debounceTime(600),
        filter((companyName: string) => companyName?.length >= 3),
        tap(() => (this.companyLoading = true)),
        switchMap((companyName: string) => this.isfStateService.searchByCompany({ name: companyName, type: this.isfEntity.entityCode })),
      )
      .subscribe((values) => {
        this.isfEntities = values;
        this.companyLoading = false;
        this.cd.detectChanges();
      });
  }

  private searchEntityByIdentifier() {
    return fromEvent(this.identifierInputElement.nativeElement, 'keyup')
      .pipe(
        distinctUntilChanged(),
        debounceTime(600),
        map(() => this.formGroup.get('entityIdentifier').value),
        filter((identifier: string) => identifier?.length >= 3),
        tap(() => (this.identifierLoading = true)),
        switchMap((identifier: string) =>
          this.isfStateService.searchByCompany({
            type: this.code as enumEntityModelCode,
            identifier,
          }),
        ),
      )
      .subscribe((values: any[] | null) => {
        this.identifierSearchResult = values;
        this.identifierLoading = false;
      });
  }

  private updateIdentifierQualMask() {
    return this.formGroup
      .get('entityIdentifierQualifier')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe((qual: enumIdentifierQualifier) => {
        if (!qual) {
          qual = enumIdentifierQualifier.EIN;
        }

        this.identifierItemMask =
          this.maskArray[EnumHelper.getEnumKeyByValue(enumIdentifierQualifier, qual)] || this.maskArray[enumIdentifierQualifier.EIN];

        this.cd.markForCheck();
      });
  }

  private updateValueByDataSource() {
    return this.dataSourceControl.valueChanges.subscribe((value) => {
      if (this.isfEntity.id === value) {
        // clear unused data
        const data = { ...this.formGroup.value };
        for (const prop of Object.getOwnPropertyNames(data)) {
          if (prop !== 'id') {
            data[prop] = data[prop] instanceof Object ? {} : null;
          } else {
            data[prop] = data[prop] && this.isfEntity.entityCode === this.formGroup.get('entityCode')?.value ? data[prop] : null;
          }
        }

        if ([enumEntityModelCode.IMPORTER, enumEntityModelCode.CONSIGNEE, enumEntityModelCode.BUYER].includes(this.isfEntity.entityCode)) {
          data.entityIdentifierQualifier = this.formGroup.value?.entityIdentifierQualifier;
          data.entityIdentifier = this.formGroup.value?.entityIdentifier;
        }

        // check form reset and value set
        this.formGroup.patchValue(
          { ...data, ...this.isfEntity, dataSource: value, name: this.formGroup.value?.name },
          { emitEvent: false },
        );

        this.cd.detectChanges();
      }
    });
  }
}
